import type { ConfirmInput, MemberCheckLine } from '@aera/schema';
import { tidy } from '@aera/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorNote } from '../components/ui.js';
import { Footer, Page } from './Chrome.js';

type Decision = { confirmed: boolean | null; correction: string };

/**
 * 我们听到的是: five templated lines (no model, no latency), each with 对 / 不对 and a one-line correction.
 * The corrections go into the context before the brief is written, and the client brief opens with them.
 * Confirming leaves any line the client did not touch as right.
 */
export function MemberCheck({
  lines,
  onConfirm,
  onBack,
}: {
  lines: MemberCheckLine[];
  onConfirm: (input: ConfirmInput) => Promise<unknown>;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [decisions, setDecisions] = useState<Record<string, Decision>>(() =>
    Object.fromEntries(lines.map((l) => [l.key, { confirmed: l.confirmed, correction: l.correction ?? '' }])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const skipped = useRef(false);

  useEffect(() => {
    if (lines.length === 0 && !skipped.current) {
      skipped.current = true;
      // A failure (answers not saved yet) leaves the confirm button to try again.
      onConfirm({ lines: [] }).catch(() => setError(t('common.error')));
    }
  }, [lines.length, onConfirm, t]);

  const set = (key: string, patch: Partial<Decision>) =>
    setDecisions((d) => ({ ...d, [key]: { ...(d[key] ?? { confirmed: null, correction: '' }), ...patch } }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        lines: lines.map((l) => {
          const d = decisions[l.key] ?? { confirmed: null, correction: '' };
          const confirmed = d.confirmed ?? true;
          const correction = !confirmed ? d.correction.trim() : '';
          return { key: l.key, confirmed, ...(correction ? { correction } : {}) };
        }),
      });
    } catch {
      setError(t('common.error'));
      setBusy(false);
    }
  };

  return (
    <>
      <Page>
        <h1 className="text-[26px] font-semibold tracking-[-0.01em]">{t('memberCheck.title')}</h1>
        <p className="mt-2 text-[15px] text-ink-2">{tidy(t('memberCheck.sub'))}</p>
        <ul className="mt-6 space-y-3">
          {lines.map((line) => {
            const d = decisions[line.key] ?? { confirmed: null, correction: '' };
            return (
              <li key={line.key} className="rounded-2xl border border-hair p-4">
                <p id={`mc-${line.key}`} className="text-[16px] leading-relaxed">
                  {tidy(line.text)}
                </p>
                <div role="radiogroup" aria-labelledby={`mc-${line.key}`} className="mt-3 flex gap-2">
                  {[
                    { v: true, label: t('memberCheck.yes') },
                    { v: false, label: t('memberCheck.no') },
                  ].map((o) => (
                    <button
                      key={String(o.v)}
                      type="button"
                      role="radio"
                      aria-checked={d.confirmed === o.v}
                      onClick={() => set(line.key, { confirmed: o.v })}
                      className="chip min-h-[44px] rounded-full border border-hair px-5 text-[15px]"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {d.confirmed === false && (
                  <input
                    type="text"
                    autoFocus
                    maxLength={300}
                    aria-label={`${line.text} · ${t('memberCheck.correctionPlaceholder')}`}
                    value={d.correction}
                    placeholder={t('memberCheck.correctionPlaceholder')}
                    onChange={(e) => set(line.key, { correction: e.target.value })}
                    className="mt-3 w-full rounded-2xl border border-hair bg-white px-4 py-3 text-[16px] outline-none focus:border-indigo"
                  />
                )}
              </li>
            );
          })}
        </ul>
        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
      </Page>
      <Footer onBack={onBack} onNext={() => void submit()} busy={busy} nextLabel={t('memberCheck.confirm')} />
    </>
  );
}
